import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useFlagsReady } from '../../../../lib/connect';
import { projectDataClient } from '../../../../lib/connect/clients';
export function flattenSections(nodes) {
    return nodes.flatMap((node)=>[
            node,
            ...flattenSections(node.children)
        ]);
}
export function useOutlineSections(projectId, volumeId, enabled = true) {
    const ready = useFlagsReady();
    return useQuery({
        queryKey: [
            'outline-sections',
            projectId,
            volumeId ?? ''
        ],
        enabled: !!projectId && ready && enabled,
        queryFn: async ()=>(await projectDataClient.getSectionMap({
                projectId,
                volumeId
            })).sections
    });
}
export function useReorderOutlineSections(projectId, volumeId) {
    const cache = useQueryClient();
    return useMutation({
        mutationFn: async (sectionIds)=>{
            const response = await projectDataClient.getSectionMap({
                projectId,
                volumeId
            });
            const sections = flattenSections(response.sections);
            const first = sections.find((section)=>section.sectionId === sectionIds[0]);
            const siblings = sections.filter((section)=>section.parentSectionId === first?.parentSectionId);
            if (!first || new Set(sectionIds).size !== sectionIds.length || siblings.length !== sectionIds.length || siblings.some((section)=>!sectionIds.includes(section.sectionId))) {
                throw new Error('The outline changed. Reload before reordering.');
            }
            // The existing RPC writes one row at a time. Retrying the same desired
            // order completes a partial save while retaining current editable fields.
            for (const [sortOrder, sectionId] of sectionIds.entries()){
                const section = siblings.find((sibling)=>sibling.sectionId === sectionId);
                if (!section) throw new Error('The outline changed. Reload before reordering.');
                if (section.sortOrder === sortOrder) continue;
                await projectDataClient.updateOutlineSection({
                    projectId,
                    volumeId,
                    sectionId,
                    sortOrder,
                    title: section.title,
                    description: section.description,
                    parentSectionId: section.parentSectionId
                });
            }
        },
        onSettled: ()=>cache.invalidateQueries({
                queryKey: [
                    'outline-sections',
                    projectId
                ]
            })
    });
}
/** The legacy RPC replaces all editable fields. Retain fields absent from this edit. */ export function useUpdateOutlineSection(projectId, volumeId) {
    const cache = useQueryClient();
    return useMutation({
        mutationFn: async ({ sectionId, updates })=>{
            const response = await projectDataClient.getSectionMap({
                projectId,
                volumeId
            });
            const current = flattenSections(response.sections).find((section)=>section.sectionId === sectionId);
            if (!current) throw new Error('This section is no longer available. Reload the outline.');
            return projectDataClient.updateOutlineSection({
                projectId,
                sectionId,
                volumeId,
                title: updates.title ?? current.title,
                description: updates.description ?? current.description,
                parentSectionId: updates.parentSectionId ?? current.parentSectionId,
                sortOrder: updates.sortOrder ?? current.sortOrder
            });
        },
        onSuccess: ()=>cache.invalidateQueries({
                queryKey: [
                    'outline-sections',
                    projectId
                ]
            })
    });
}
